import { DecimalPipe } from '@angular/common';
import {
  Directive,
  HostListener,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';

@Directive({
  standalone: true,
  selector: 'input[noiceNumber]',
  providers: [DecimalPipe],
})
export class NoiceNumberDirective implements AfterViewChecked {
  inputRef: HTMLInputElement;

  // Allow decimal numbers. The \. is only allowed once to occur
  private regex: RegExp = new RegExp(/^[0-9]+(\.[0-9]*){0,1}$/g);

  //control key state
  isCtrlPressed: boolean = false;

  // Allow key codes for special events. Reflect :
  // Backspace, tab, end, home
  private specialKeys: Array<string> = [
    'Backspace',
    'Delete',
    'Tab',
    'End',
    'Home',
    'ArrowRight',
    'ArrowDown',
    'ArrowLeft',
    'ArrowUp',
    '.',
  ];

  constructor(elementRef: ElementRef, private decimalPipe: DecimalPipe) {
    this.inputRef = elementRef.nativeElement;
  }

  ngAfterViewChecked() {
    if (!this.inputRef.value) {
      return;
    }

    if (isNaN(+this.inputRef.value)) {
      return;
    }

    const lastCharIndex = this.inputRef.value.length - 1;

    if (this.inputRef.value[this.inputRef.value.length - 1] === '.') {
      return;
    }

    this.inputRef.value =
      this.decimalPipe
        .transform(
          +this.inputRef.value.toString().split(',').join(''),
          '.0-2',
          'en-US'
        )
        ?.toString() ?? '';
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: any) {
    //#region NumberOnly
    this.isCtrlPressed = false;

    //prevent double decimal separator
    if (event.key === '.' && event.target.value.includes('.')) {
      event.preventDefault();
      return;
    }

    if (event.key === ',') {
      event.preventDefault();
      return;
    }

    //allow and track ctrl, command and windows keys
    if (['Control', 'Meta'].includes(event.key)) {
      this.isCtrlPressed = true;
    }
    //allow ctrl actions/shortcuts (copy, paste, select all, cut,...)
    if (this.isCtrlPressed) {
      return;
    }

    // Do not use event.keycode this is deprecated.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
    if (!this.specialKeys.includes(event.key)) {
      let current: string = this.inputRef.value;
      // We need this because the current value on the DOM element
      // is not yet updated with the value from this event
      let next: string = current
        ? current?.concat(event.key).replaceAll(/(,)/gi, '')
        : '';
      if (next && !String(next).match(this.regex)) {
        event?.preventDefault();
      }
    }

    //#endregion NumberOnly
  }
  @HostListener('input', ['$event'])
  onInput(event: any) {
    // Check if inputRef is properly initialized
    if (!this.inputRef) {
      return;
    }

    const targetAttributes = event.target.attributes as NamedNodeMap;
    if (
      !targetAttributes.getNamedItem('opsnumber') &&
      !targetAttributes.getNamedItem('noicenumber')
    ) {
      return;
    }

    //m4 cashflow description field input gets caught by this listener even if the directive isnt applied, this is a workaround
    // TODO: find out another way to prevent this unwanted behaviour
    const target = event.target;
    if (!target.value) {
      return;
    }

    const lastCharIndex = target.value.length - 1;

    if (target.value[lastCharIndex] === '.') {
      if (target.value.slice(0, -1).includes('.')) {
        target.value = target.value.slice(0, -1);
      }
      return;
    }

    let posStart = target.selectionStart;
    const oldSeparatorCount = target.value.toString().split(',').length - 1;

    // Check if inputRef and its value exist before proceeding
    if (!this.inputRef || !this.inputRef.value) {
      return;
    }

    this.inputRef.value = this.decimalPipe.transform(
      +this.inputRef.value.toString().replaceAll(',', ''),
      '.0-2',
      'en-US'
    );

    if (
      !event.data &&
      event.inputType === 'deleteContentBackward' &&
      this.inputRef.value &&
      this.inputRef.value.charAt(posStart) === ','
    ) {
      //    console.log(this.inputRef.value)
      this.inputRef.value =
        this.inputRef.value.slice(0, posStart - 1) +
        this.inputRef.value.slice(posStart);
      posStart--;
    }
    //    console.log(this.inputRef.value.charAt(posStart))
    if (
      !event.data &&
      event.inputType === 'deleteContentForward' &&
      this.inputRef.value &&
      this.inputRef.value.charAt(posStart) === ','
    ) {
      this.inputRef.value =
        this.inputRef.value.slice(0, posStart + 1) +
        this.inputRef.value.slice(posStart + 2);
      posStart++;
    }

    const newSeparatorCount = target.value.toString().split(',').length - 1;
    const offset = newSeparatorCount - oldSeparatorCount;

    // Check if inputRef exists before calling setSelectionRange
    if (this.inputRef && typeof this.inputRef.setSelectionRange === 'function') {
      this.inputRef.setSelectionRange(posStart + offset, posStart + offset);
    }
  }
}
